/**
 * GA4 Client Plugin
 *
 * Handles client-side Google Analytics 4 tracking via gtag().
 * Fires events with proper session attribution.
 *
 * IMPORTANT: Only fires for events where 'ga4-client' is true in EventRegistry.
 * SubmitApplication goes to GA4 Measurement Protocol (server-side) instead.
 */

import { ga4Log as log } from "../../logger";
import { shouldFireTo } from "../registry";
import type { EventName, PluginResult, UnifiedEvent } from "../types";
import { BasePlugin } from "./base";

// GA4 event name mapping (internal → GA4 standard)
// NOTE: Business context matters!
// - "Lead" = job application (from job seeker to employer) - NOT our business lead
// - "VerifyEmail" = employer verifying their email - THIS is our business lead
// - "CompleteRegistration" = employer completed signup - THIS is our sign_up
const GA4_EVENT_MAPPING: Partial<Record<EventName, string>> = {
  PageView: "page_view",
  Lead: "job_application",         // Job seeker applied - custom event (not generate_lead!)
  Purchase: "purchase",
  CompleteRegistration: "sign_up", // Employer signed up
  BeginCheckout: "begin_checkout",
  SubmitApplication: "submit_application",
  VerifyEmail: "generate_lead",    // Employer verified email - our actual lead!
  ViewJobListing: "view_item",
  ViewJobPreview: "view_item",
};

/**
 * Convert PascalCase to snake_case for custom events
 */
function toSnakeCase(str: string): string {
  return str.replace(/([A-Z])/g, "_$1").toLowerCase().slice(1);
}

/**
 * Map internal event name to GA4 event name
 */
function mapEventName(event: EventName): string {
  return GA4_EVENT_MAPPING[event] || toSnakeCase(event);
}

export class GA4ClientPlugin extends BasePlugin {
  name = "ga4-client";
  private initialized = false;

  async initialize(): Promise<void> {
    // Scripts are loaded in layout.tsx - just wait for gtag
    if (typeof window !== "undefined") {
      await this.waitFor(() => typeof window.gtag === "function", 5000);
      this.initialized = true;
    }
  }

  supports(event: EventName): boolean {
    // Only fire for events configured for ga4-client
    return shouldFireTo(event, "ga4-client");
  }

  isReady(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof window.gtag === "function" &&
      this.initialized
    );
  }

  async track(event: UnifiedEvent): Promise<PluginResult> {
    if (!this.isReady()) {
      return this.error("gtag_not_ready");
    }

    try {
      const ga4EventName = mapEventName(event.eventName);

      // Build GA4 params with proper session attribution
      // Strip reserved GA4 traffic source params from event.params to prevent
      // accidental session attribution overrides (e.g. { source: "homepage" }
      // from tracking calls would override the real traffic source)
      const { source: _s, medium: _m, campaign: _c, ...safeParams } = event.params as Record<string, unknown>;
      const ga4Params: Record<string, unknown> = {
        ...safeParams,

        // CRITICAL: Session params for attribution
        ...(event.attribution.ga4SessionId && {
          session_id: event.attribution.ga4SessionId,
        }),
        ...(event.attribution.ga4SessionNumber && {
          session_number: event.attribution.ga4SessionNumber,
        }),
        engagement_time_msec:
          (event.params.engagement_time_msec as number) || 100,

        // Traffic source (GA4 standard param names - no utm_ prefix)
        ...(event.attribution.utmSource && {
          source: event.attribution.utmSource,
        }),
        ...(event.attribution.utmMedium && {
          medium: event.attribution.utmMedium,
        }),
        ...(event.attribution.utmCampaign && {
          campaign: event.attribution.utmCampaign,
        }),
        ...(event.attribution.utmTerm && { term: event.attribution.utmTerm }),
        ...(event.attribution.utmContent && {
          content: event.attribution.utmContent,
        }),
        ...(event.attribution.utmId && {
          campaign_id: event.attribution.utmId,
        }),

        // Also include utm_ prefixed for reporting
        ...(event.attribution.utmSource && {
          utm_source: event.attribution.utmSource,
        }),
        ...(event.attribution.utmMedium && {
          utm_medium: event.attribution.utmMedium,
        }),
        ...(event.attribution.utmCampaign && {
          utm_campaign: event.attribution.utmCampaign,
        }),

        // Click IDs
        ...(event.attribution.gclid && { gclid: event.attribution.gclid }),

        // Page context
        page_location: event.attribution.pageUrl,
        page_title: event.attribution.pageTitle,
        language: event.attribution.language,

        // Deduplication key
        deduplication_key: event.eventId,

        // User ID if available
        ...(event.context?.applicantId && {
          user_id: event.context.applicantId,
        }),
      };

      // Fire client-side gtag event
      window.gtag("event", ga4EventName, ga4Params);

      log.info(`Tracked: ${ga4EventName}`, {
        sessionId: event.attribution.ga4SessionId,
        sessionNumber: event.attribution.ga4SessionNumber,
      });

      return this.success({ eventName: ga4EventName });
    } catch (err) {
      log.error("Tracking error", err);
      return this.error(err instanceof Error ? err.message : "unknown_error");
    }
  }
}
